/**
 * Academy curriculum — the operator learning path (gap G19).
 *
 * The operator does not need more documentation. Fourteen articles already
 * exist in four languages; the problem is that the dashboard contained no link
 * to any of them, so an operator had to leave the product to find help.
 * Documentation people must exit the product to read is documentation they do
 * not read.
 *
 * So this file holds NO article text. Every lesson points at an existing help
 * slug, and the dashboard links out to it. Duplicating the prose here would
 * create a second copy to keep in sync in four languages, and the copy inside
 * the product would be the one that rots.
 *
 * ## Sequence, not a table of contents
 *
 * The articles are reference material, ordered for lookup. A learning path is
 * ordered by when someone needs the thing — and gated on what they have
 * actually done, exactly like progressive disclosure (ADR-0030). Teaching
 * OFFERS; it never gates. A lesson that is not yet unlocked is not withheld
 * from a curious operator, it simply is not pushed at them.
 *
 * Pure data plus pure functions: no I/O, no clock, no React.
 */

/** Which audience a track teaches. */
export type TrackId = 'operator' | 'agent';

/** A section groups lessons into something an operator can feel done with. */
export interface AcademySection {
  readonly id: string;
  /** i18n key for the section name. Copy lives in the catalog, not here. */
  readonly titleKey: string;
  /**
   * What the operator can claim once the section is complete. Shown as
   * competence feedback — "You've got the queue board" — because finishing
   * something and being told you finished it are different experiences.
   */
  readonly masteryKey: string;
  readonly lessons: readonly AcademyLesson[];
}

export interface AcademyLesson {
  readonly id: string;
  readonly titleKey: string;
  /**
   * Slug of the EXISTING help article this lesson sends the operator to.
   * Must match a file in `apps/marketing/src/content/help/{locale}/`.
   */
  readonly helpSlug: string;
  /**
   * Dashboard route this lesson belongs beside, so it can be offered in
   * context rather than only from an index nobody visits.
   */
  readonly route?: string;
  /**
   * What the operator must have done for this lesson to be offered.
   *
   * `null` means "from day one". Anything else is a real event the product
   * already records — teaching arrives when the screen it describes has
   * something on it.
   */
  readonly unlockedBy: AcademyUnlock | null;
}

/** Events the product already tracks, reused as teaching triggers. */
export type AcademyUnlock =
  | 'first_outlet'
  | 'first_wash'
  | 'first_paid_ticket'
  | 'ten_customers'
  | 'loyalty_configured';

/** What the operator has done. Mirrors the disclosure signals. */
export interface AcademyProgressSignals {
  readonly hasOutlet: boolean;
  readonly washesRecorded: number;
  readonly paidTickets: number;
  readonly customersKnown: number;
  readonly loyaltyConfigured: boolean;
}

/**
 * The operator track.
 *
 * Ordered by the moment of need, not by the manual's own ordering. The first
 * section carries an operator to the thing that actually convinces them — a
 * real customer paying without cash changing hands — because everything before
 * that is cost they are absorbing on faith.
 */
export const OPERATOR_TRACK: {
  readonly trackId: TrackId;
  readonly sections: readonly AcademySection[];
} = {
  trackId: 'operator',
  sections: [
    {
      id: 'first-wash',
      titleKey: 'academy.operator.section.firstWash.title',
      masteryKey: 'academy.operator.section.firstWash.mastery',
      lessons: [
        {
          id: 'getting-started',
          titleKey: 'academy.operator.lesson.gettingStarted',
          helpSlug: 'getting-started',
          unlockedBy: null,
        },
        {
          id: 'catalog',
          titleKey: 'academy.operator.lesson.catalog',
          helpSlug: 'catalog',
          route: '/catalog',
          unlockedBy: null,
        },
        {
          id: 'queue-board',
          titleKey: 'academy.operator.lesson.queueBoard',
          helpSlug: 'queue-board',
          route: '/queue',
          unlockedBy: 'first_outlet',
        },
        {
          id: 'pos',
          titleKey: 'academy.operator.lesson.pos',
          helpSlug: 'pos',
          route: '/pos',
          unlockedBy: 'first_wash',
        },
      ],
    },
    {
      id: 'running-it',
      titleKey: 'academy.operator.section.runningIt.title',
      masteryKey: 'academy.operator.section.runningIt.mastery',
      lessons: [
        {
          id: 'staff',
          titleKey: 'academy.operator.lesson.staff',
          helpSlug: 'staff',
          route: '/staff',
          unlockedBy: 'first_paid_ticket',
        },
        {
          id: 'branding',
          titleKey: 'academy.operator.lesson.branding',
          helpSlug: 'branding',
          route: '/branding/hours',
          unlockedBy: 'first_paid_ticket',
        },
        {
          id: 'billing',
          titleKey: 'academy.operator.lesson.billing',
          helpSlug: 'billing',
          route: '/billing',
          unlockedBy: 'first_paid_ticket',
        },
      ],
    },
    {
      id: 'keeping-them',
      titleKey: 'academy.operator.section.keepingThem.title',
      masteryKey: 'academy.operator.section.keepingThem.mastery',
      lessons: [
        {
          id: 'loyalty',
          titleKey: 'academy.operator.lesson.loyalty',
          helpSlug: 'loyalty',
          route: '/loyalty/settings',
          unlockedBy: 'ten_customers',
        },
        {
          id: 'promotions',
          titleKey: 'academy.operator.lesson.promotions',
          helpSlug: 'promotions',
          route: '/loyalty/promotions',
          unlockedBy: 'loyalty_configured',
        },
        {
          id: 'broadcasts',
          titleKey: 'academy.operator.lesson.broadcasts',
          helpSlug: 'broadcasts',
          route: '/marketing',
          unlockedBy: 'ten_customers',
        },
        {
          id: 'notifications',
          titleKey: 'academy.operator.lesson.notifications',
          helpSlug: 'notifications',
          route: '/notifications',
          unlockedBy: 'ten_customers',
        },
      ],
    },
  ],
};

/** Whether a lesson's unlock condition is met. */
export function isLessonUnlocked(lesson: AcademyLesson, signals: AcademyProgressSignals): boolean {
  switch (lesson.unlockedBy) {
    case null:
      return true;
    case 'first_outlet':
      return signals.hasOutlet;
    case 'first_wash':
      return signals.washesRecorded > 0;
    case 'first_paid_ticket':
      return signals.paidTickets > 0;
    case 'ten_customers':
      return signals.customersKnown >= 10;
    case 'loyalty_configured':
      return signals.loyaltyConfigured;
    default:
      // An unlock nobody has implemented yet OFFERS the lesson rather than
      // hiding it. Failing closed would silently remove teaching, and nobody
      // notices missing help until they need it.
      return true;
  }
}

/** A section's completion, from the set of lesson ids the operator has read. */
export interface SectionProgress {
  readonly sectionId: string;
  readonly total: number;
  readonly done: number;
  /** True the moment every lesson in the section is read. */
  readonly complete: boolean;
  /** Lessons currently offered — unlocked and unread. */
  readonly nextUp: readonly AcademyLesson[];
}

/**
 * Progress across the operator track.
 *
 * `completedLessonIds` is a set rather than a count so an operator who reads
 * lessons out of order — which they will, because they follow their own
 * problems — gets credit for exactly what they read.
 */
export function trackProgress(
  completedLessonIds: ReadonlySet<string>,
  signals: AcademyProgressSignals,
): readonly SectionProgress[] {
  return OPERATOR_TRACK.sections.map((section) => {
    const done = section.lessons.filter((l) => completedLessonIds.has(l.id)).length;
    return {
      sectionId: section.id,
      total: section.lessons.length,
      done,
      complete: done === section.lessons.length,
      nextUp: section.lessons.filter(
        (l) => !completedLessonIds.has(l.id) && isLessonUnlocked(l, signals),
      ),
    };
  });
}

/** Every lesson attached to one dashboard route, unlocked and unread. */
export function lessonsForRoute(
  route: string,
  completedLessonIds: ReadonlySet<string>,
  signals: AcademyProgressSignals,
): readonly AcademyLesson[] {
  return OPERATOR_TRACK.sections
    .flatMap((s) => s.lessons)
    .filter(
      (l) => l.route === route && !completedLessonIds.has(l.id) && isLessonUnlocked(l, signals),
    );
}
